class CustomerExperiencesColumns extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopBehavior = this.dataset.desktopBehavior || 'autoscroll_manual';
    this.mobileBehavior = this.dataset.mobileBehavior || 'autoplay_swipe';
    this.autoSpeed = Number.parseFloat(this.dataset.autoscrollSpeed || '22') || 22;
    this.mobileDelay = (Number.parseInt(this.dataset.mobileAutoplayDelay || '4', 10) || 4) * 1000;
    this.desktopColumns = Array.from(this.querySelectorAll('[data-column]')).map((column) => {
      const viewport = column.querySelector('[data-column-viewport]');
      const track = column.querySelector('[data-column-track]');
      const stack = column.querySelector('[data-column-stack]');

      return {
        column,
        viewport,
        track,
        stack,
        clone: null,
        baseHeight: 0,
        loopDistance: 0,
        maxOffset: 0,
        gap: 0,
        offset: 0,
        pointerId: null,
        pointerStartY: 0,
        startOffset: 0,
        isDragging: false,
      };
    });
    this.mobileViewport = this.querySelector('[data-mobile-viewport]');
    this.mobileTrack = this.querySelector('[data-mobile-track]');
    this.mobileSlides = Array.from(this.querySelectorAll('[data-mobile-slide]')).filter(
      (slide) => slide instanceof HTMLElement
    );
    this.mobileScrollTimeout = null;
    this.mobileAutoTimer = null;
    this.lastAnimationTimestamp = null;
    this.isDesktop = this.desktopMediaQuery.matches;
    this.refreshRequested = false;

    this.handleAnimationFrame = this.handleAnimationFrame.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleMobileScroll = this.handleMobileScroll.bind(this);
    this.handleMobilePointerDown = this.handleMobilePointerDown.bind(this);

    this.desktopColumns.forEach((state) => this.bindDesktopColumn(state));
    this.mobileViewport?.addEventListener('scroll', this.handleMobileScroll, { passive: true });
    this.mobileViewport?.addEventListener('pointerdown', this.handleMobilePointerDown, { passive: true });
    this.mobileViewport?.addEventListener('touchstart', this.handleMobilePointerDown, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleViewportResize);
      this.resizeObserver.observe(this);

      this.desktopColumns.forEach((state) => {
        state.viewport && this.resizeObserver.observe(state.viewport);
        state.stack && this.resizeObserver.observe(state.stack);
      });

      this.mobileViewport && this.resizeObserver.observe(this.mobileViewport);
      this.mobileTrack && this.resizeObserver.observe(this.mobileTrack);
    } else {
      window.addEventListener('resize', this.handleViewportResize);
    }

    this.addMediaQueryListener(this.desktopMediaQuery, this.handleMediaChange);
    this.addMediaQueryListener(this.reduceMotionQuery, this.handleMediaChange);

    this.refresh();
    this.animationFrame = window.requestAnimationFrame(this.handleAnimationFrame);
  }

  disconnectedCallback() {
    this.desktopColumns.forEach((state) => this.unbindDesktopColumn(state));
    this.mobileViewport?.removeEventListener('scroll', this.handleMobileScroll);
    this.mobileViewport?.removeEventListener('pointerdown', this.handleMobilePointerDown);
    this.mobileViewport?.removeEventListener('touchstart', this.handleMobilePointerDown);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleViewportResize);
    this.removeMediaQueryListener(this.desktopMediaQuery, this.handleMediaChange);
    this.removeMediaQueryListener(this.reduceMotionQuery, this.handleMediaChange);
    window.cancelAnimationFrame(this.animationFrame);
    window.clearTimeout(this.mobileScrollTimeout);
    window.clearTimeout(this.mobileRestartTimeout);
    window.clearInterval(this.mobileAutoTimer);
  }

  addMediaQueryListener(mediaQuery, listener) {
    if (!mediaQuery) return;

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(listener);
    }
  }

  removeMediaQueryListener(mediaQuery, listener) {
    if (!mediaQuery) return;

    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', listener);
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(listener);
    }
  }

  bindDesktopColumn(state) {
    const viewport = state.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    state.handleWheel = (event) => {
      if (!this.isDesktop || !this.desktopAllowsManual()) return;

      event.preventDefault();
      state.offset += event.deltaY;
      this.applyDesktopTransform(state);
    };

    state.handlePointerDown = (event) => {
      if (!this.isDesktop || !this.desktopAllowsManual() || event.button !== 0) return;

      state.isDragging = true;
      state.pointerId = event.pointerId;
      state.pointerStartY = event.clientY;
      state.startOffset = state.offset;
      state.column.classList.add('is-dragging');
      viewport.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };

    state.handlePointerMove = (event) => {
      if (!state.isDragging || state.pointerId !== event.pointerId) return;

      const deltaY = event.clientY - state.pointerStartY;
      state.offset = state.startOffset - deltaY;
      this.applyDesktopTransform(state);
    };

    state.handlePointerUp = (event) => {
      if (!state.isDragging || state.pointerId !== event.pointerId) return;

      state.isDragging = false;
      state.pointerId = null;
      state.column.classList.remove('is-dragging');
      viewport.releasePointerCapture?.(event.pointerId);
    };

    viewport.addEventListener('wheel', state.handleWheel, { passive: false });
    viewport.addEventListener('pointerdown', state.handlePointerDown);
    viewport.addEventListener('pointermove', state.handlePointerMove);
    viewport.addEventListener('pointerup', state.handlePointerUp);
    viewport.addEventListener('pointercancel', state.handlePointerUp);
    viewport.addEventListener('lostpointercapture', state.handlePointerUp);
  }

  unbindDesktopColumn(state) {
    const viewport = state.viewport;
    if (!(viewport instanceof HTMLElement)) return;

    viewport.removeEventListener('wheel', state.handleWheel);
    viewport.removeEventListener('pointerdown', state.handlePointerDown);
    viewport.removeEventListener('pointermove', state.handlePointerMove);
    viewport.removeEventListener('pointerup', state.handlePointerUp);
    viewport.removeEventListener('pointercancel', state.handlePointerUp);
    viewport.removeEventListener('lostpointercapture', state.handlePointerUp);
  }

  handleViewportResize() {
    this.requestRefresh();
  }

  handleMediaChange() {
    this.requestRefresh();
  }

  requestRefresh() {
    if (this.refreshRequested) return;

    this.refreshRequested = true;
    window.requestAnimationFrame(() => {
      this.refreshRequested = false;
      this.refresh();
    });
  }

  refresh() {
    this.isDesktop = this.desktopMediaQuery.matches;

    if (this.isDesktop) {
      this.refreshDesktop();
      this.stopMobileAutoplay();
    } else {
      this.resetDesktopColumns();
      this.refreshMobile();
    }
  }

  refreshDesktop() {
    this.desktopColumns.forEach((state) => {
      const autoEnabled = this.desktopAllowsAuto();
      const manualEnabled = this.desktopAllowsManual();
      const canLoop = this.canLoopColumn(state);

      state.column.classList.toggle('is-manual', manualEnabled);
      state.column.classList.toggle('is-autoscroll', autoEnabled);

      if (canLoop) {
        this.ensureDesktopClone(state);
      } else {
        this.removeDesktopClone(state);
      }

      this.updateDesktopMeasurements(state);

      if (canLoop && state.loopDistance > 0) {
        if (state.column.dataset.direction === 'down' && state.offset === 0) {
          state.offset = Math.max(state.loopDistance - 1, 0);
        }
      }

      this.applyDesktopTransform(state);
    });
  }

  resetDesktopColumns() {
    this.desktopColumns.forEach((state) => {
      state.column.classList.remove('is-dragging');
      state.column.classList.remove('is-manual');
      state.column.classList.remove('is-autoscroll');
      state.isDragging = false;
      state.offset = 0;
      this.removeDesktopClone(state);
      if (state.track instanceof HTMLElement) {
        state.track.style.transform = 'translate3d(0, 0, 0)';
      }
    });
  }

  canLoopColumn(state) {
    if (!(state.viewport instanceof HTMLElement) || !(state.stack instanceof HTMLElement)) return false;
    if (state.stack.childElementCount < 1) return false;
    return state.stack.offsetHeight > state.viewport.clientHeight + 8;
  }

  ensureDesktopClone(state) {
    if (!(state.track instanceof HTMLElement) || !(state.stack instanceof HTMLElement) || state.clone) return;

    state.clone = state.stack.cloneNode(true);
    state.clone.setAttribute('aria-hidden', 'true');
    state.clone.dataset.clone = 'true';
    state.track.appendChild(state.clone);
  }

  removeDesktopClone(state) {
    if (!state.clone) return;

    state.clone.remove();
    state.clone = null;
  }

  updateDesktopMeasurements(state) {
    if (!(state.viewport instanceof HTMLElement) || !(state.stack instanceof HTMLElement) || !(state.track instanceof HTMLElement)) return;

    const trackStyles = window.getComputedStyle(state.track);
    const gap = Number.parseFloat(trackStyles.rowGap || trackStyles.gap || '0') || 0;
    state.gap = gap;
    state.baseHeight = state.stack.offsetHeight;
    state.loopDistance = state.clone ? state.baseHeight + gap : 0;
    state.maxOffset = Math.max(state.baseHeight - state.viewport.clientHeight, 0);
  }

  normalizeOffset(value, distance) {
    if (distance <= 0) return 0;

    let nextValue = value % distance;
    if (nextValue < 0) nextValue += distance;
    return nextValue;
  }

  applyDesktopTransform(state) {
    if (!(state.track instanceof HTMLElement)) return;

    if (state.clone && state.loopDistance > 0) {
      state.offset = this.normalizeOffset(state.offset, state.loopDistance);
    } else {
      state.offset = Math.min(Math.max(state.offset, 0), state.maxOffset);
    }

    state.track.style.transform = `translate3d(0, ${-state.offset}px, 0)`;
  }

  desktopAllowsManual() {
    return this.desktopBehavior === 'manual' || this.desktopBehavior === 'autoscroll_manual';
  }

  desktopAllowsAuto() {
    return !this.reduceMotionQuery.matches && (this.desktopBehavior === 'autoscroll' || this.desktopBehavior === 'autoscroll_manual');
  }

  refreshMobile() {
    if (!this.mobileViewport || this.mobileSlides.length === 0) return;

    if (this.mobileBehavior === 'autoplay_swipe' && !this.reduceMotionQuery.matches && this.mobileSlides.length > 1) {
      this.startMobileAutoplay();
    } else {
      this.stopMobileAutoplay();
    }
  }

  handleMobilePointerDown() {
    if (!this.isDesktop) {
      this.restartMobileAutoplay();
    }
  }

  handleMobileScroll() {
    if (this.isDesktop) return;

    window.clearTimeout(this.mobileScrollTimeout);
    this.mobileScrollTimeout = window.setTimeout(() => {
      this.currentMobileIndex = this.getClosestMobileIndex();
    }, 80);

    this.restartMobileAutoplay();
  }

  getClosestMobileIndex() {
    if (!(this.mobileViewport instanceof HTMLElement) || this.mobileSlides.length === 0) return 0;

    const currentLeft = this.mobileViewport.scrollLeft;
    let closestIndex = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;

    this.mobileSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - currentLeft);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  scrollToMobileIndex(index) {
    if (!(this.mobileViewport instanceof HTMLElement) || this.mobileSlides.length === 0) return;

    const safeIndex = ((index % this.mobileSlides.length) + this.mobileSlides.length) % this.mobileSlides.length;
    const targetSlide = this.mobileSlides[safeIndex];
    if (!(targetSlide instanceof HTMLElement)) return;

    this.currentMobileIndex = safeIndex;
    this.mobileViewport.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth',
    });
  }

  startMobileAutoplay() {
    this.stopMobileAutoplay();

    this.mobileAutoTimer = window.setInterval(() => {
      const nextIndex = (this.getClosestMobileIndex() + 1) % this.mobileSlides.length;
      this.scrollToMobileIndex(nextIndex);
    }, this.mobileDelay);
  }

  stopMobileAutoplay() {
    window.clearInterval(this.mobileAutoTimer);
    this.mobileAutoTimer = null;
  }

  restartMobileAutoplay() {
    if (this.mobileBehavior !== 'autoplay_swipe' || this.reduceMotionQuery.matches || this.isDesktop) return;

    this.stopMobileAutoplay();
    window.clearTimeout(this.mobileRestartTimeout);
    this.mobileRestartTimeout = window.setTimeout(() => this.startMobileAutoplay(), this.mobileDelay);
  }

  handleAnimationFrame(timestamp) {
    if (this.isDesktop && this.desktopAllowsAuto()) {
      if (this.lastAnimationTimestamp == null) {
        this.lastAnimationTimestamp = timestamp;
      }

      const delta = Math.min((timestamp - this.lastAnimationTimestamp) / 1000, 0.05);
      this.lastAnimationTimestamp = timestamp;

      this.desktopColumns.forEach((state) => {
        if (!(state.viewport instanceof HTMLElement) || state.loopDistance <= 0 || !state.clone || state.isDragging) return;

        const directionMultiplier = state.column.dataset.direction === 'down' ? -1 : 1;
        state.offset += directionMultiplier * this.autoSpeed * delta;
        this.applyDesktopTransform(state);
      });
    } else {
      this.lastAnimationTimestamp = null;
    }

    this.animationFrame = window.requestAnimationFrame(this.handleAnimationFrame);
  }
}

if (!customElements.get('customer-experiences-columns')) {
  customElements.define('customer-experiences-columns', CustomerExperiencesColumns);
}
